// Libs
import classNames from 'classnames/bind';
import { yupResolver } from '@hookform/resolvers/yup';
import { useTranslation } from 'react-i18next';
import { Box, Button, Stack } from '@mui/material';
import { pdf } from '@react-pdf/renderer';
import { useEffect, useState } from 'react';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { FormProvider, useForm, UseFormReturn } from 'react-hook-form';
// Components, Layouts, Pages
import { InfoInvoiceForm, InvoicePdf } from '~/components/module/invoice';
import { BaseToolbar, Loading, UnderDevelopment } from '~/components/common';
// Others
import { IBodyUpdate } from '~/utils/interface/common';
import { IOptionClient } from '~/utils/interface/client';
import { useAppDispatch } from '~/utils/redux/hooks';
import { MethodEnum, StatusInvoiceEnum } from '~/utils/enum/common';
import { IFormInvoice, IInvoiceItems, IInvoicePdf } from '~/utils/interface/invoice';
import { createInvoice, getDetailInvoice, updateInvoice } from '~/thunks/invoice/invoiceThunk';
import { cleanObject, createFormData, getFullName, removeEmptyObjects } from '~/utils/helper/common';
import { BACK_TO_PREVIOUS_PAGE, DEFAULT_INVOICE_FILE_NAME_PDF, EMPTY_STRING } from '~/utils/constants/common';
import { defaultValuesInvoiceForm, schema } from './helper';
// Styles, images, icons
import styles from './InvoiceForm.module.scss';
import { icons } from '~/assets';

type Props = {};

const cx = classNames.bind(styles);

const InvoiceForm = (props: Props) => {
  //#region Destructuring Props
  const {} = props;
  //#endregion Destructuring Props

  //#region Declare Hook
  const { t } = useTranslation(['admin/invoice']);
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const clientId = searchParams.get('clientId');
  const dispatch = useAppDispatch();
  const { id: invoiceId } = useParams();

  const {
    control,
    setValue,
    handleSubmit,
    watch,
    getValues,
    reset,
    formState: { errors, isDirty },
  } = useForm<IFormInvoice>({
    resolver: yupResolver(schema(t)),
    defaultValues: defaultValuesInvoiceForm,
  });
  //#endregion Declare Hook

  //#region Selector
  //#endregion Selector

  //#region Declare State
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [isShowUnderDevelopment, setIsShowUnderDevelopment] = useState<boolean>(false);
  const [customerInfo, setCustomerInfo] = useState<IOptionClient>();
  const [clientNameDefault, setClientNameDefault] = useState<string>(EMPTY_STRING);
  const [orderNameDefault, setOrderNameDefault] = useState<string>(EMPTY_STRING);
  //#endregion Declare State

  //#region Declare Memo
  //#endregion Declare Memo

  //#region Implement Hook
  useEffect(() => {
    if (!clientId) return;

    setValue('clientId', clientId);
  }, [clientId]);

  useEffect(() => {
    if (!invoiceId) return;

    handleGetDetailInvoice(invoiceId);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [invoiceId]);
  //#endregion Implement Hook

  //#region Handle Function
  const handleGetDetailInvoice = (invoiceId: string) => {
    setIsLoading(true);

    const queryDetailForm = {
      id: invoiceId,
      params: { method: MethodEnum.UPDATE },
    };

    dispatch(getDetailInvoice(queryDetailForm))
      .unwrap()
      .then((res) => {
        if (!res?.data) return;

        const { id, ...remainingData } = res?.data;
        const body: IFormInvoice = {
          orderId: remainingData?.order?.id || EMPTY_STRING,
          clientId: remainingData?.client?.id || EMPTY_STRING,
          discountRate: remainingData?.discountRate || EMPTY_STRING,
          discountType: remainingData?.discountType || EMPTY_STRING,
          invoiceItems: remainingData?.invoiceItems?.map((item) => {
            const newItem = cleanObject(item) as IInvoiceItems;
            return {
              ...newItem,
              idDetail: item?.id || EMPTY_STRING,
              productId: `${item?.inventoryId}_${item?.variantId}`,
            };
          }),
          paymentTerms: remainingData?.paymentTerms || EMPTY_STRING,
          invoiceDate: remainingData?.invoiceDate || EMPTY_STRING,
          dueDate: remainingData?.dueDate || EMPTY_STRING,
          status: remainingData?.status || EMPTY_STRING,
          subtotal: remainingData?.subtotal || EMPTY_STRING,
          taxRate: remainingData?.taxRate || EMPTY_STRING,
          taxAmount: remainingData?.taxAmount || EMPTY_STRING,
          total: remainingData?.total || EMPTY_STRING,
          amountPaid: remainingData?.amountPaid || EMPTY_STRING,
          balanceDue: remainingData?.balanceDue || EMPTY_STRING,
          note: remainingData?.note || EMPTY_STRING,
        };

        setClientNameDefault(
          getFullName({ firstName: remainingData?.client?.firstName, lastName: remainingData?.client?.lastName })
        );
        setOrderNameDefault(remainingData?.order?.orderNumber);

        reset(body);
      })
      .catch((_error) => {})
      .finally(() => {
        setIsLoading(false);
      });
  };

  const handleFormInvoice = (data: IFormInvoice, status?: string) => {
    if (invoiceId) {
      handleEditInvoice(data, status);
    } else {
      handleCreateInvoice(data, status);
    }

    setIsLoading(true);
  };

  const handleCreateInvoice = async (data: IFormInvoice, status?: string) => {
    const convertData: IFormInvoice = removeEmptyObjects({
      ...data,
      status,
      balanceDue: data.total,
      invoiceItems:
        data.invoiceItems?.map(({ productId, idDetail, type, isShowOnPrint, ...item }) => ({ ...item })) || [],
    }) as IFormInvoice;

    const formData = await createFormData(convertData);

    if (status === StatusInvoiceEnum.PENDING) {
      const dataPdf = handleGetDataInvoicePdf(data);

      const pdfBlob = await generatePdfBlob(dataPdf);

      if (!pdfBlob) return;

      formData.append('invoicePdf', pdfBlob, DEFAULT_INVOICE_FILE_NAME_PDF);
    }

    dispatch(createInvoice(formData))
      .unwrap()
      .then((res) => {
        handleCancel();
      })
      .catch((err) => {})
      .finally(() => setIsLoading(false));
  };

  const generatePdfBlob = async (data: IInvoicePdf): Promise<Blob | null> => {
    try {
      return await pdf(<InvoicePdf invoicePdf={data} />).toBlob();
    } catch (error) {
      console.error('Generate PDF failed: ', error);
      return null;
    }
  };

  const handleEditInvoice = async (data: IFormInvoice, status?: string) => {
    const convertData: IFormInvoice = removeEmptyObjects({
      ...data,
      status,
      balanceDue: data.total,
      invoiceItems:
        data.invoiceItems?.map(({ productId, idDetail, type, isShowOnPrint, ...item }) => ({ ...item })) || [],
    }) as IFormInvoice;

    const formData = await createFormData(convertData);

    if (status === StatusInvoiceEnum.PENDING) {
      const dataPdf = handleGetDataInvoicePdf(data);

      const pdfBlob = await generatePdfBlob(dataPdf);

      if (!pdfBlob) return;

      formData.append('invoicePdf', pdfBlob, DEFAULT_INVOICE_FILE_NAME_PDF);
    }

    const params: IBodyUpdate<FormData> = {
      id: invoiceId || EMPTY_STRING,
      body: formData,
    };

    dispatch(updateInvoice(params))
      .unwrap()
      .then(() => {
        handleCancel();
      })
      .catch(() => {})
      .finally(() => {
        setIsLoading(false);
      });
  };

  const handleShowUnderDevelopment = () => {
    setIsShowUnderDevelopment(!isShowUnderDevelopment);
  };

  const handleCancel = () => {
    navigate(BACK_TO_PREVIOUS_PAGE);
    reset();
  };

  const handleGetDataInvoicePdf = (data: IFormInvoice) => {
    const dataDetail: IInvoicePdf = {
      clientName: customerInfo?.label || EMPTY_STRING,
      dueDate: data?.dueDate,
      invoices: data?.invoiceItems || [],
      subtotal: data?.subtotal || EMPTY_STRING,
      taxAmount: data?.taxAmount || EMPTY_STRING,
      discountRate: data?.discountRate || EMPTY_STRING,
      total: data?.total || EMPTY_STRING,
    };

    return dataDetail;
  };
  //#endregion Handle Function

  return (
    <Box id='invoiceFormPage' className={cx('container')}>
      <BaseToolbar
        title={t(invoiceId ? 'invoice_form_title_update_invoice' : 'invoice_form_title_create_invoice')}
        isBack
      />

      <FormProvider
        {...({
          control,
          watch,
          reset,
          setValue,
          getValues,
          formState: { errors, isDirty },
        } as UseFormReturn<IFormInvoice>)}
      >
        <Stack className={cx('body')}>
          <InfoInvoiceForm
            clientNameDefault={clientNameDefault}
            orderNameDefault={orderNameDefault}
            customerInfo={customerInfo}
            setCustomerInfo={setCustomerInfo}
          />
        </Stack>
      </FormProvider>

      <Stack direction={'row'} spacing={3} justifyContent={'flex-end'}>
        <Button
          variant='outlined'
          className={cx('buttonSaveDraft')}
          onClick={handleSubmit((data) => handleFormInvoice(data, StatusInvoiceEnum.DRAFT))}
          startIcon={<img src={icons.commonIconSaveDraft} alt={t('common_alt_img')} />}
        >
          {t('invoice_form_title_save_as_draft')}
        </Button>

        <Button
          variant='contained'
          className={cx('buttonSendInvoice')}
          onClick={handleSubmit((data) => handleFormInvoice(data, StatusInvoiceEnum.PENDING))}
          startIcon={<img src={icons.commonIconSendQuote} alt={t('common_alt_img')} />}
        >
          {t('invoice_form_title_save_invoice')}
        </Button>
      </Stack>

      {isLoading && <Loading />}

      {isShowUnderDevelopment && <UnderDevelopment onClose={handleShowUnderDevelopment} />}
    </Box>
  );
};

export default InvoiceForm;
